import { useCallback, useEffect, useState } from 'react'

import { formatTime, parseTime } from './utils'

import type { TimeValue } from './types'

interface UseTimeStateInput {
  value: TimeValue | null | undefined
  format: '12' | '24'
  showSeconds: boolean
}

export interface TimeState {
  inputValue: string
  setInputValue: React.Dispatch<React.SetStateAction<string>>
  selectedHour: number | null
  setSelectedHour: React.Dispatch<React.SetStateAction<number | null>>
  selectedMinute: number | null
  setSelectedMinute: React.Dispatch<React.SetStateAction<number | null>>
  selectedPeriod: 'AM' | 'PM'
  setSelectedPeriod: React.Dispatch<React.SetStateAction<'AM' | 'PM'>>
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export function useTimePickerState({ value, format, showSeconds }: UseTimeStateInput): TimeState {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedHour, setSelectedHour] = useState<number | null>(null)
  const [selectedMinute, setSelectedMinute] = useState<number | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>(
    value && value.hours >= 12 ? 'PM' : 'AM',
  )

  useEffect(() => {
    if (value) {
      setInputValue(formatTime(value, format, showSeconds))
      setSelectedHour(format === '12' ? (value.hours % 12 || 12) : value.hours)
      setSelectedMinute(value.minutes)
      setSelectedPeriod(value.hours >= 12 ? 'PM' : 'AM')
    } else {
      setInputValue('')
      setSelectedHour(null)
      setSelectedMinute(null)
    }
  }, [value, format, showSeconds])

  return {
    inputValue, setInputValue,
    selectedHour, setSelectedHour,
    selectedMinute, setSelectedMinute,
    selectedPeriod, setSelectedPeriod,
    isOpen, setIsOpen,
  }
}

export function useTimeOutsideClick(
  containerRef: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  isMobile: boolean,
  setIsOpen: (v: boolean) => void,
): void {
  useEffect(() => {
    if (isMobile) { return }
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isMobile, containerRef, setIsOpen])
}

export function useIsTimeInRange(minTime?: TimeValue, maxTime?: TimeValue): (time: TimeValue) => boolean {
  return useCallback((time: TimeValue): boolean => {
    const timeMinutes = time.hours * 60 + time.minutes
    if (minTime) {
      const minMinutes = minTime.hours * 60 + minTime.minutes
      if (timeMinutes < minMinutes) { return false }
    }
    if (maxTime) {
      const maxMinutes = maxTime.hours * 60 + maxTime.minutes
      if (timeMinutes > maxMinutes) { return false }
    }
    return true
  }, [minTime, maxTime])
}

interface UpdateTimeInput {
  hour: number
  minute: number
  period: 'AM' | 'PM'
  format: '12' | '24'
  isTimeInRange: (time: TimeValue) => boolean
  onChange?: (time: TimeValue | null) => void
}

export function applyUpdateTime({ hour, minute, period, format, isTimeInRange, onChange }: UpdateTimeInput): void {
  let hours24 = hour
  if (format === '12') {
    if (period === 'PM' && hour !== 12) { hours24 = hour + 12 }
    else if (period === 'AM' && hour === 12) { hours24 = 0 }
  }
  const time: TimeValue = { hours: hours24, minutes: minute }
  if (isTimeInRange(time)) { onChange?.(time) }
}

interface UseSelectionHandlersInput {
  state: TimeState
  format: '12' | '24'
  isTimeInRange: (time: TimeValue) => boolean
  onChange?: (time: TimeValue | null) => void
  minuteStep: number
}

export interface SelectionHandlers {
  handleHourSelect: (hour: number) => void
  handleMinuteSelect: (minute: number) => void
  handlePeriodSelect: (period: 'AM' | 'PM') => void
  handleNowClick: () => void
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleInputBlur: (value: TimeValue | null | undefined, showSeconds: boolean) => void
}

interface BuildPickHandlersInput {
  state: TimeState
  format: '12' | '24'
  isTimeInRange: (time: TimeValue) => boolean
  onChange?: (time: TimeValue | null) => void
  updateTime: (hour: number, minute: number, period: 'AM' | 'PM') => void
  minuteStep: number
}

function buildPickHandlers(input: BuildPickHandlersInput): Pick<SelectionHandlers, 'handleHourSelect' | 'handleMinuteSelect' | 'handlePeriodSelect' | 'handleNowClick'> {
  const { state, format, isTimeInRange, onChange, updateTime, minuteStep } = input
  return {
    handleHourSelect: (hour) => {
      state.setSelectedHour(hour)
      /* v8 ignore next */
      if (state.selectedMinute !== null) { updateTime(hour, state.selectedMinute, state.selectedPeriod) }
    },
    handleMinuteSelect: (minute) => {
      state.setSelectedMinute(minute)
      if (state.selectedHour !== null) {
        updateTime(state.selectedHour, minute, state.selectedPeriod)
        if (format === '24') { state.setIsOpen(false) }
      }
    },
    handlePeriodSelect: (period) => {
      state.setSelectedPeriod(period)
      if (state.selectedHour !== null && state.selectedMinute !== null) {
        updateTime(state.selectedHour, state.selectedMinute, period)
        state.setIsOpen(false)
      }
    },
    handleNowClick: () => {
      const now = new Date()
      const hours = now.getHours()
      const minutes = Math.floor(now.getMinutes() / minuteStep) * minuteStep
      const time: TimeValue = { hours, minutes }
      if (isTimeInRange(time)) { onChange?.(time); state.setIsOpen(false) }
    },
  }
}

interface BuildInputHandlersInput {
  state: TimeState
  format: '12' | '24'
  isTimeInRange: (time: TimeValue) => boolean
  onChange?: (time: TimeValue | null) => void
}

function buildInputHandlers(input: BuildInputHandlersInput): Pick<SelectionHandlers, 'handleInputChange' | 'handleInputBlur'> {
  const { state, format, isTimeInRange, onChange } = input
  return {
    handleInputChange: (e) => {
      const newValue = e.target.value
      state.setInputValue(newValue)
      const parsed = parseTime(newValue, format)
      if (parsed && isTimeInRange(parsed)) { onChange?.(parsed) }
    },
    handleInputBlur: (currentValue, showSeconds) => {
      if (!state.inputValue) {
        onChange?.(null)
        return
      }
      const parsed = parseTime(state.inputValue, format)
      /* v8 ignore next */
      if (parsed && isTimeInRange(parsed)) { return }
      if (currentValue) {
        state.setInputValue(formatTime(currentValue, format, showSeconds))
      } else {
        state.setInputValue('')
        onChange?.(null)
      }
    },
  }
}

export function useSelectionHandlers(input: UseSelectionHandlersInput): SelectionHandlers {
  const { state, format, isTimeInRange, onChange, minuteStep } = input
  const updateTime = useCallback((hour: number, minute: number, period: 'AM' | 'PM') => {
    applyUpdateTime({ hour, minute, period, format, isTimeInRange, onChange })
  }, [format, isTimeInRange, onChange])

  return {
    ...buildPickHandlers({ state, format, isTimeInRange, onChange, updateTime, minuteStep }),
    ...buildInputHandlers({ state, format, isTimeInRange, onChange }),
  }
}
